<script setup lang="ts">
import { ref, computed, watch, type ComponentPublicInstance } from "vue";
import { Modal } from "bootstrap";
import { useAdminUpdateUserRole } from "@/queries/orgs";
import { useSessionStore } from "@/stores/session-store";
import { useAlertStore } from "@/stores/alerts-store";
import { storeToRefs } from "pinia";
import type { IUser } from "@/types";

const props = defineProps<{
  show: boolean;
  user: IUser | null;
  orgId?: string; // Optional: if provided, update user in this org instead of current org
}>();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "userUpdated"): void;
}>();

const { adminUpdateUserRole } = useAdminUpdateUserRole();
const sessionStore = useSessionStore();
const alertStore = useAlertStore();
const { session } = storeToRefs(sessionStore);

const modalRef = ref<HTMLElement | null>(null);
const modalInstance = ref<Modal | null>(null);

const formData = ref({
  role: "OrgMember",
});

const allRoles = [
  { value: "SuperAdmin", label: "Super Admin" },
  { value: "OrgAdmin", label: "Organization Admin" },
  { value: "OrgMember", label: "Organization Member" },
  { value: "Partner", label: "Partner" },
];

// Filter out SuperAdmin role for non-SuperAdmins
const availableRoles = computed(() => {
  if (session.value?.isSuperAdmin) {
    return allRoles;
  }
  return allRoles.filter(r => r.value !== "SuperAdmin");
});

const isSubmitting = ref(false);
const errorMessage = ref("");

watch(
  () => props.show,
  (newVal) => {
    if (newVal && props.user) {
      modalInstance.value?.show();
      // Initialize form with user's current role, defaulting to OrgMember
      // if the stored value isn't one of the current allowed roles (e.g. legacy data).
      const incoming = props.user.role;
      const matches = incoming && availableRoles.value.some(r => r.value === incoming);
      formData.value = {
        role: matches ? incoming! : "OrgMember",
      };
      errorMessage.value = "";
    } else {
      modalInstance.value?.hide();
    }
  }
);

const handleSubmit = () => {
  if (!props.user) return;

  if (!formData.value.role) {
    errorMessage.value = "Please select a role.";
    return;
  }

  errorMessage.value = "";
  isSubmitting.value = true;

  const targetOrgId = props.orgId || session.value.currentOrgId;

  adminUpdateUserRole(
    { orgId: targetOrgId, userId: props.user.id, role: formData.value.role },
    {
      onSuccess: () => {
        alertStore.addSuccess("User Updated", `User role has been successfully updated to ${formData.value.role}`);
        emit("userUpdated");
        closeModal();
      },
      onError: (e: any) => { errorMessage.value = e?.userMessage ?? "Failed to update user role"; },
      onSettled: () => { isSubmitting.value = false; },
    }
  );
};

const closeModal = () => {
  emit("update:show", false);
};

const onModalHidden = () => {
  emit("update:show", false);
};

// Initialize modal
const initModal = (el: Element | ComponentPublicInstance | null) => {
  if (el && el instanceof HTMLElement && !modalInstance.value) {
    modalRef.value = el;
    modalInstance.value = new Modal(el);
    el.addEventListener("hidden.bs.modal", onModalHidden);
  }
};
</script>

<template>
  <div
    :ref="initModal"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="editUserModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="editUserModalLabel">Edit User Role</h5>
          <button
            type="button"
            class="btn-close"
            @click="closeModal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div v-if="errorMessage" class="alert alert-danger" role="alert">
            {{ errorMessage }}
          </div>

          <div v-if="user" class="mb-3">
            <label class="form-label fw-bold">User</label>
            <div class="card">
              <div class="card-body">
                <p class="mb-1">
                  <strong>Name:</strong> {{ user.firstName }} {{ user.lastName }}
                </p>
                <p class="mb-0"><strong>Email:</strong> {{ user.email }}</p>
              </div>
            </div>
          </div>

          <!-- ============================================================
               Role update – own inline form so it doesn't interact with
               the password section below.
               ============================================================ -->
          <form @submit.prevent="handleSubmit">
            <div class="mb-3">
              <label for="role" class="form-label">Role</label>
              <select
                class="form-select"
                id="role"
                v-model="formData.role"
                required
              >
                <option
                  v-for="role in availableRoles"
                  :key="role.value"
                  :value="role.value"
                >
                  {{ role.label }}
                </option>
              </select>
              <div class="form-text">Select the new role for this user.</div>
            </div>

            <button
              type="submit"
              class="btn btn-primary btn-sm"
              :disabled="isSubmitting"
            >
              <span
                v-if="isSubmitting"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ isSubmitting ? "Updating..." : "Update Role" }}
            </button>
          </form>

        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeModal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
